import { axiosInstance } from './api';

/**
 * On-demand admin-UI tunnel through the jetson to a device's web admin
 * (Hikvision ISAPI, MikroTik WebFig, etc.). Drives the
 * `<AdminTunnelButton>` on Equipment-tab device detail (issue #34).
 *
 * `AdminTunnelCapability` mirrors the BE
 * `services/plugins/equipment_discovery/vendors/admin_ports.py` return
 * shape. Non-null = BE knows where to forward → render the button.
 * Null/undefined = no button (per #34 spec).
 *
 * The issuance endpoint is `POST /equipment_discovery/<kind>/<id>/admin-tunnel/`
 * and returns a single-use https://gateway/tunnel/<token>/ URL. The
 * tunnel BE itself lands in a follow-up commit — until then this call
 * 404s and the button surfaces a clear error toast.
 */

export interface AdminTunnelCapability {
  scheme: 'http' | 'https';
  port: number;
  label: string;
}

export type AdminTunnelKind = 'nvrs' | 'cameras' | 'routers';

export interface AdminTunnelIssueResponse {
  url: string;
  expires_at: string;
}

// URL convention mirrors the existing endpoint shapes:
//   nvrs    → /equipment_discovery/nvrs/<id>/admin-tunnel/        (id = NVR pk)
//   cameras → /equipment_discovery/cameras/<id>/admin-tunnel/     (id = Camera pk)
//   routers → /equipment_discovery/zones/<id>/router/admin-tunnel/ (id = zone pk,
//             since Router is OneToOne(Zone) and listed zone-scoped)
const tunnelUrl = (kind: AdminTunnelKind, id: number | string) =>
  kind === 'routers'
    ? `/equipment_discovery/zones/${id}/router/admin-tunnel/`
    : `/equipment_discovery/${kind}/${id}/admin-tunnel/`;

export const openAdminTunnel = (kind: AdminTunnelKind, id: number | string) =>
  axiosInstance.post<AdminTunnelIssueResponse>(tunnelUrl(kind, id));
